import styled from 'styled-components'
import React, {
  type ChangeEventHandler,
  useEffect,
  useState,
  type FocusEvent,
  type MouseEvent,
  type KeyboardEvent,
} from 'react'
import { AtlasColor, AtlasCaption } from '@justomx/atlas-components'

interface TextAreaAtomProps {
  label: string
  onNewValue?: (value: string) => void
  onChange?: (value: string) => void
  onChangeFullEvent?: (value: React.ChangeEvent<HTMLTextAreaElement>) => void
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void
  onClick?: (event: MouseEvent<HTMLTextAreaElement>) => void
  onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void
  onKeyPress?: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  "data-test"?: string
  name?: string
  value?: string
  hasErrors?: boolean
  autocomplete?: string
  tabIndex?: number
  supportingText?: string
  readonly?: boolean
  disabled?: boolean
  rows?: number
}

const TextArea = styled.textarea<{ $hasErrors?: boolean }>`
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid
    ${({ $hasErrors }) =>
      $hasErrors ? AtlasColor.Error : AtlasColor.Neutral20};
  background-color: transparent;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${AtlasColor.Neutral20};
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: ${({ $hasErrors }) =>
      $hasErrors ? AtlasColor.Error : AtlasColor.Neutral00};
    color: ${AtlasColor.Neutral00};
  }

  &:not(:placeholder-shown) + label {
    top: -6px;
    font-size: 12px;
    padding: 0 4px;
    background-color: ${AtlasColor.Background};
    color: ${({ $hasErrors }) =>
      $hasErrors ? AtlasColor.Error : AtlasColor.Neutral00};
  }

  &::placeholder {
    color: ${AtlasColor.Neutral20};
  }
`

const Label = styled.label<{ $hasErrors: boolean }>`
  color: ${({ $hasErrors }) =>
    $hasErrors ? AtlasColor.Error : AtlasColor.Neutral20};
  position: absolute;
  pointer-events: none;
  left: 16px;
  top: 18px;
  transition: 0.2s ease all;

  ${TextArea}:focus ~ & {
    top: -6px;
    font-size: 12px;
    padding: 0 4px;
    background-color: ${AtlasColor.Background};
    color: ${({ $hasErrors }) =>
      $hasErrors ? AtlasColor.Error : AtlasColor.Neutral00};
  }
`

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`

const HelperText = styled(AtlasCaption)<{ $hasErrors: boolean }>`
  display: block;
  padding: 4px 16px 0px 16px;
  color: ${({ $hasErrors }) =>
    $hasErrors ? AtlasColor.Error : AtlasColor.Neutral20};
`

const onKeyUp = (onNewValue?: (value: string) => void) =>
  (event: KeyboardEvent<HTMLTextAreaElement>) => {
    return event.currentTarget.value &&
      (event.key === 'Enter' || event.keyCode === 13)
      ? onNewValue?.(event.currentTarget.value)
      : null
  }

export const TextAreaMolecule = (props: TextAreaAtomProps) => {
  const {
    label,
    onNewValue,
    onChange,
    onChangeFullEvent,
    onBlur,
    onClick,
    onFocus,
    onKeyPress,
    hasErrors = false,
    autocomplete,
    tabIndex,
    supportingText,
    readonly,
    disabled,
    rows = 3,
  } = props

  const [value, setValue] = useState<string>(props.value ?? '')
  const _keyUp = onKeyUp(onNewValue)

  useEffect(() => {
    setValue(props.value ?? '')
  }, [props.value])

  const _change: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    setValue(event.target.value)
    onChange?.(event.target.value)
    onChangeFullEvent?.(event)
  }

  return (
    <TextAreaWrapper>
      <TextArea
        placeholder=""
        value={value}
        onKeyUp={_keyUp}
        onChange={_change}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        data-test={props['data-test']}
        name={props.name}
        autoComplete={autocomplete}
        tabIndex={tabIndex}
        $hasErrors={hasErrors}
        readOnly={readonly}
        disabled={disabled}
        rows={rows}
      />
      <Label $hasErrors={hasErrors}>
        <AtlasCaption data-test={props['data-test'] + '-label'}>
          {label}
        </AtlasCaption>
      </Label>
      {supportingText && (
        <HelperText $hasErrors={hasErrors}>
          {supportingText}
        </HelperText>
      )}
    </TextAreaWrapper>
  )
}
